// Ported from /home/lk/website/dashboard/src/notification_modal.jsx:620-947 (the per-notification action set and the "action taken → handled" contract). India hooks removed: blocking modal stack, client-side status writes and multi-call assignment fan-out, operator-to-field bridge calls on the India telephony rail, vendor bank payouts, follow-up rule re-trigger calls, city scoping.
/**
 * Alert inbox model: every alert (from P5's escalation inbox or a live stream event) maps to one group, and each group
 * has a fixed action set, exactly as India's modal picked its UI from `notification.actions`. Differences (US rails):
 *   - no status select: "tech declined" → Reassign opens Dispatch (the server's assign command does every side effect);
 *   - "tech stopped" → Call tech (P4 bridge), Mark on break (server command), Open dispatch;
 *   - an action that finishes the work acks the alert (India `onNotificationHandled(id, action)`); navigation doesn't —
 *     the alert clears itself when the matching event arrives (job reassigned, tech moving again).
 */
import type { StreamEvent } from '@app/core/types';
import type { IconName } from '@ui/icons';

export type AlertGroup = 'tech_stopped' | 'tech_declined' | 'estimate_declined' | 'anomaly' | 'parts_pending' | 'job';
export type ActionKey = 'call_tech' | 'mark_break' | 'open_dispatch' | 'reassign' | 'open_job' | 'open_parts';

export interface Alert {
  /** Dedupe key: one alert per group + subject (job or tech). */
  key: string;
  /** Server inbox id (ackable on the server); null for alerts seen only on the live stream. */
  id: string | null;
  group: AlertGroup;
  kind: string;
  job_id: string | null;
  tech_id: string | null;
  at: string;
  minutes: number | null;
  source: 'inbox' | 'live';
}

export interface AlertAction { key: ActionKey; label: string; icon: IconName; primary?: boolean; perm: string[]; acks: boolean }

/** P5 escalation kinds (dispatch/service.js + the US workflow) and live event kinds → group. */
export function groupOf(kind: string): AlertGroup {
  if (/stationary|stopped|going_check|^check_(no_answer|late)|eta_exceeded/.test(kind)) return 'tech_stopped';
  if (/declin|denied|needs_dispatcher|unassigned|check_cant|offer_expired/.test(kind)) return kind.startsWith('estimate') ? 'estimate_declined' : 'tech_declined';
  if (/estimate/.test(kind)) return 'estimate_declined';
  if (/parts|restock|stock|purchase/.test(kind)) return 'parts_pending';
  if (/anomaly/.test(kind)) return 'anomaly';
  return 'job';
}

const A = {
  call_tech: { key: 'call_tech', label: 'Call tech', icon: 'phone', perm: ['jobs.edit', 'jobs.assign'], acks: false },
  mark_break: { key: 'mark_break', label: 'Mark on break', icon: 'clock', perm: ['jobs.assign'], acks: true },
  open_dispatch: { key: 'open_dispatch', label: 'Open dispatch', icon: 'map', perm: ['dispatch.view_map', 'jobs.assign'], acks: false },
  reassign: { key: 'reassign', label: 'Reassign', icon: 'users', perm: ['jobs.assign'], acks: false },
  open_job: { key: 'open_job', label: 'Open job', icon: 'wrench', perm: ['jobs.view'], acks: false },
  open_parts: { key: 'open_parts', label: 'Open parts', icon: 'box', perm: ['parts.view'], acks: false },
} satisfies Record<ActionKey, AlertAction>;

/** The action set per group (India: `notification.actions` → which buttons the modal renders). */
export function actionsFor(a: Alert): AlertAction[] {
  switch (a.group) {
    case 'tech_stopped': return [{ ...A.call_tech, primary: true }, A.mark_break, A.open_dispatch].filter((x) => a.tech_id || x.key === 'open_dispatch');
    case 'tech_declined': return a.job_id ? [{ ...A.reassign, primary: true }, A.open_job] : [A.open_dispatch];
    case 'parts_pending': return [{ ...A.open_parts, primary: true }];
    case 'estimate_declined': case 'anomaly': case 'job': return a.job_id ? [{ ...A.open_job, primary: true }] : [];
  }
}

export function hrefFor(action: ActionKey, a: Alert): string | null {
  switch (action) {
    case 'reassign': return a.job_id ? `/dispatch?job=${encodeURIComponent(a.job_id)}` : '/dispatch';
    case 'open_dispatch': return a.job_id ? `/dispatch?job=${encodeURIComponent(a.job_id)}` : '/dispatch';
    case 'open_job': return a.job_id ? `/jobs/${encodeURIComponent(a.job_id)}` : null;
    case 'open_parts': return '/parts';
    default: return null;
  }
}

const keyOf = (group: AlertGroup, jobId: string | null, techId: string | null, fallback: string) =>
  `${group}:${group === 'tech_stopped' ? techId ?? jobId ?? fallback : jobId ?? techId ?? fallback}`;

export function fromInbox(r: { id: string; job_id: string | null; tech_id: string | null; kind: string; created_at: string; data: Record<string, unknown> }): Alert {
  const group = groupOf(r.kind);
  const minutes = Number(r.data?.minutes);
  return { key: keyOf(group, r.job_id, r.tech_id, r.id), id: r.id, group, kind: r.kind, job_id: r.job_id, tech_id: r.tech_id, at: r.created_at, minutes: Number.isFinite(minutes) ? minutes : null, source: 'inbox' };
}

const s = (v: unknown) => (typeof v === 'string' && v ? v : null);

/** A live stream event that raises an alert (the inbox catches up on its next refetch). */
export function fromEvent(e: StreamEvent): Alert | null {
  const d = e.data;
  const job = s(d.job_id) ?? (e.entity === 'job' ? s(e.entity_id) : null);
  const tech = s(d.tech_id) ?? (e.entity === 'tech' ? s(e.entity_id) : null);
  const mk = (kind: string, jobId = job, techId = tech): Alert => {
    const group = groupOf(kind);
    return { key: keyOf(group, jobId, techId, e.id), id: null, group, kind, job_id: jobId, tech_id: techId, at: e.at, minutes: Number.isFinite(Number(d.minutes)) ? Number(d.minutes) : null, source: 'live' };
  };
  switch (e.type) {
    case 'tech.alert': case 'tech.stationary': return mk('tech_stationary');
    case 'job.status_changed': return d.to === 'MECHANIC DENIED' ? mk('tech_declined') : null;
    case 'job.declined': return mk('tech_declined');
    case 'estimate.declined': return mk('estimate_declined');
    case 'ops.anomaly': return mk(`anomaly_${s(d.kind) ?? 'job'}`);
    case 'dispatch.escalated': case 'sla.breached': return mk(s(d.kind) ?? 'escalation');
    case 'stock.below_reorder': return mk('parts_reorder', null, null);
    default: return null;
  }
}

/** True when an event means the situation behind a live alert is over (so it clears without an ack). */
export function clearedBy(e: StreamEvent, a: Alert): boolean {
  const d = e.data;
  if (a.group === 'tech_stopped') return (e.type === 'tech.state' && d.tech_id === a.tech_id && d.state !== 'alert') || (e.type === 'tech.check_outcome' && d.tech_id === a.tech_id && d.outcome === 'moving');
  if (a.group === 'tech_declined') return (e.type === 'job.assigned' && d.job_id === a.job_id) || (e.type === 'job.status_changed' && d.job_id === a.job_id && typeof d.to === 'string' && d.to.startsWith('CANCELLED'));
  return false;
}
